// The Compare page as data (task eval_admin_runs): each metric's difference
// with "not distinguishable" marked where the comparison cannot resolve it,
// and the words for why a model or a case is not compared. Pure: tested in
// compareView.test.ts.
//
// The server computes the difference as a − b (compare.ts): run a is the one
// under review, run b the reference (another run, or the pinned baseline), so
// a positive difference means run a scored higher.

import { formatFigure, formatInterval, formatPoints, metricLabel } from './format';
import type { EvalMetricDelta, EvalModelComparison, EvalModelDiff } from './types';

export type DeltaVerdict = 'higher' | 'lower' | 'not_distinguishable' | 'no_data';

export interface DeltaView {
  key: string;
  metric: string;
  split: string;
  a: string;
  b: string;
  /** "+8.3 pts", or '—' when either run has no figure for the metric. */
  difference: string;
  /** The interval of a − b in points: "−2.1 to +18.4 pts". '' when there is none. */
  interval: string;
  verdict: DeltaVerdict;
  /** The words the verdict column shows. */
  verdictText: string;
}

function pts(v: number): string {
  return formatPoints(v).replace(/ pts$/, '');
}

/**
 * One metric's row. A difference the comparison cannot resolve (its interval
 * holds zero, it is one case or less, or it is inside what the run resolves)
 * reads "not distinguishable", never higher or lower.
 */
export function deltaView(d: EvalMetricDelta): DeltaView {
  const base = {
    key: `${d.metric}:${d.split}`,
    metric: metricLabel(d.metric),
    split: d.split === 'all' ? 'all cases' : d.split,
    a: formatFigure(d.a),
    b: formatFigure(d.b),
  };
  const diff = d.difference;
  if (diff === null) {
    return {
      ...base,
      difference: '—',
      interval: '',
      verdict: 'no_data',
      verdictText:
        d.a === null || d.b === null ? 'Only one run has this metric' : 'No shared cases',
    };
  }
  const interval = `${pts(diff.lo)} to ${pts(diff.hi)} pts`;
  if (!diff.distinguishable) {
    return {
      ...base,
      difference: formatPoints(diff.mean),
      interval,
      verdict: 'not_distinguishable',
      verdictText: 'Not distinguishable',
    };
  }
  return {
    ...base,
    difference: formatPoints(diff.mean),
    interval,
    verdict: diff.mean > 0 ? 'higher' : 'lower',
    verdictText: diff.mean > 0 ? 'Run A higher' : 'Run A lower',
  };
}

/** The metric rows a model's comparison shows: every split the server compared, 'all' first. */
export function deltaRows(diff: EvalModelDiff): DeltaView[] {
  const order = { all: 0, train: 1, test: 2 } as const;
  return [...diff.metrics]
    .filter((m) => m.a !== null || m.b !== null)
    .sort((x, y) => order[x.split] - order[y.split])
    .map(deltaView);
}

/** Why a model of run a has no comparison. Null when it has one. */
export function missingText(m: Pick<EvalModelComparison, 'missing'>): string | null {
  switch (m.missing) {
    case 'no_baseline':
      return 'No baseline is pinned for this model on this suite and split.';
    case 'is_baseline':
      return 'This run is the pinned baseline for this model.';
    case 'not_in_b':
      return 'Run B did not run this model.';
    default:
      return null;
  }
}

const NOT_COMPARABLE_TEXT = {
  case_changed: 'the case changed between the runs',
  hash_unknown: 'a run did not record which version of the case it graded',
  not_counted: 'a run has no counted repeat of it',
} as const;

export function notComparableText(reason: keyof typeof NOT_COMPARABLE_TEXT): string {
  return NOT_COMPARABLE_TEXT[reason];
}

/** "1/3" style outcome marks for one side of a case diff: ✓ and ✗ in repeat order. */
export function outcomeMarks(outcomes: readonly boolean[]): string {
  if (outcomes.length === 0) return '—';
  return outcomes.map((o) => (o ? '✓' : '✗')).join('');
}

/** The counts a model's case diff leads with. */
export function caseDiffCounts(diff: EvalModelDiff): {
  newlyFailing: number;
  newlyPassing: number;
  stillFailing: number;
  flaky: number;
  unchanged: number;
  notComparable: number;
  onlyOne: number;
} {
  const c = diff.cases;
  return {
    newlyFailing: c.newlyFailing.length,
    newlyPassing: c.newlyPassing.length,
    stillFailing: c.stillFailing.length,
    flaky: c.flaky.length,
    unchanged: c.unchanged,
    notComparable: c.notComparable.length,
    onlyOne: c.onlyA.length + c.onlyB.length,
  };
}

/** A figure with its interval, for the compare header cells. */
export function figureWithInterval(f: EvalMetricDelta['a']): string {
  if (f === null) return '—';
  const i = formatInterval(f.ci95);
  return i.length > 0 ? `${formatFigure(f)} (${i})` : formatFigure(f);
}
